import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { PaperAirplaneIcon, SparklesIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { PortraitFrame } from '../common/editorial/EditorialControls';
import UserAvatar from '../common/UserAvatar';

const CommentSection = ({ postId, user, onComment, postTopic, postContent }) => {
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAuraThinking, setIsAuraThinking] = useState(false);
  const [comments, setComments] = useState([]);

  const fetchComments = useCallback(async () => {
    try {
      const response = await axios.get(`/api/community/posts/${postId}/comments`);
      if (response.data.status === 'success' && response.data.data) {
        setComments(response.data.data.comments || []);
      }
    } catch (error) {
      /* ignore */
    }
  }, [postId]);

  useEffect(() => {
    setComments([]);
    fetchComments();
  }, [postId, fetchComments]);

  const detectAuraMention = (text) => text.toLowerCase().includes('@aura');

  const parseAuraCommand = (text) => {
    const lowerText = text.toLowerCase();
    if (lowerText.includes('explain')) return { type: 'explain' };
    if (
      lowerText.includes('provide more information') ||
      lowerText.includes('tell me more') ||
      lowerText.includes('more info') ||
      lowerText.includes('give me more details')
    ) {
      return { type: 'more_info' };
    }
    return { type: 'question', question: text.replace(/@aura/gi, '').trim() };
  };

  const handleAuraExplain = async (parentComment) => {
    setIsAuraThinking(true);
    try {
      const response = await axios.post('/api/community/aura/explain', {
        comment_text: parentComment,
        post_topic: postTopic,
        post_content: postContent,
      });

      if (response.data.explanation) {
        await axios.post(`/api/community/posts/${postId}/comments`, {
          content: response.data.explanation,
          anonymous: false,
          is_aura_response: true,
        });
        await fetchComments();
      }
    } catch (error) {
      alert('Failed to get explanation from Aura. Please try again.');
    } finally {
      setIsAuraThinking(false);
    }
  };

  const handleAuraQuestion = async (question) => {
    setIsAuraThinking(true);
    try {
      const response = await axios.post('/api/community/aura/answer', {
        question,
        post_content: postContent,
      });

      if (response.data.answer) {
        await axios.post(`/api/community/posts/${postId}/comments`, {
          content: response.data.answer,
          anonymous: false,
          is_aura_response: true,
        });
        await fetchComments();
      }
    } catch (error) {
      alert('Failed to get answer from Aura. Please try again.');
    } finally {
      setIsAuraThinking(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!comment.trim() || isSubmitting) return;

    if (detectAuraMention(comment)) {
      const command = parseAuraCommand(comment);

      if (command.type === 'explain') {
        if (comments.length > 0) {
          await handleAuraExplain(comments[comments.length - 1].content);
        } else {
          await handleAuraQuestion(`Explain this post: ${postContent}`);
        }
        setComment('');
        return;
      }
      if (command.type === 'more_info' || command.type === 'question') {
        await handleAuraQuestion(command.question || comment);
        setComment('');
        return;
      }
    }

    setIsSubmitting(true);
    try {
      await onComment(comment.trim());
      setComment('');
      await fetchComments();
    } catch (error) {
      /* handled upstream */
    } finally {
      setIsSubmitting(false);
    }
  };

  const userInitials =
    (user?.full_name || user?.profile_name || 'You').charAt(0).toUpperCase();

  return (
    <div className="space-y-4">
      {comments.length > 0 && (
        <div className="space-y-4">
          {comments.map((c) => {
            const isAura = c.is_aura_response || c.isAuraResponse;
            return (
              <div key={c.id} className="flex gap-3">
                {isAura ? (
                  <div className="w-8 h-8 rounded-lg border border-hairline bg-pine flex items-center justify-center shrink-0 surface-dark">
                    <SparklesIcon className="w-4 h-4 text-primary" />
                  </div>
                ) : c.author?.is_verified_advisor ? (
                  <PortraitFrame
                    size="xs"
                    src={c.author?.avatar_url || c.author?.avatar}
                    alt={c.author?.name}
                    initials={c.author?.name?.charAt(0)?.toUpperCase() || 'U'}
                  />
                ) : (
                  <UserAvatar
                    userId={c.author?.user_id}
                    src={c.author?.avatar_url || c.author?.avatar}
                    alt={c.author?.name}
                    size="xs"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div
                    className={`rounded-lg p-3 border ${
                      isAura
                        ? 'aura-card border-t-2 border-t-pine light-surface'
                        : 'bg-bone border-hairline'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h4 className="text-label font-medium text-primary">{c.author?.name}</h4>
                      {isAura && (
                        <span className="badge-mark badge-mark-earned text-label">Aura</span>
                      )}
                      <span className="text-label text-faint">{c.timestamp}</span>
                    </div>
                    <p className="text-body text-primary">{c.content}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {isAuraThinking && (
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="flex gap-3"
        >
          <div className="w-8 h-8 rounded-lg border border-hairline bg-pine flex items-center justify-center shrink-0 surface-dark">
            <SparklesIcon className="w-4 h-4 text-primary" />
          </div>
          <div className="flex-1 aura-card border-t-2 border-t-pine light-surface p-3">
            <p className="text-label text-secondary">Aura is thinking…</p>
          </div>
        </motion.div>
      )}

      <form onSubmit={handleSubmit} className="flex gap-3">
        <UserAvatar
          userId={user?.user_id}
          src={user?.avatar_url || user?.avatar}
          alt={user?.full_name || user?.profile_name || 'You'}
          size="xs"
        />
        <div className="flex-1">
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Write a comment… Try @Aura explain this"
            className="input-editorial resize-none"
            rows={2}
            maxLength={500}
          />
          <div className="flex justify-between items-center mt-2">
            <span className="text-label text-faint">{comment.length}/500</span>
            <div className="flex items-center gap-2">
              {isSubmitting && <span className="text-label text-secondary">Posting…</span>}
              <button
                type="submit"
                disabled={!comment.trim() || isSubmitting}
                className="icon-btn icon-btn-active disabled:opacity-50"
                aria-label="Post comment"
              >
                <PaperAirplaneIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CommentSection;
